import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import AddToList from '../components/AddToList.jsx'
import Artwork from '../components/Artwork.jsx'
import Avatar from '../components/Avatar.jsx'
import DiscographyGraph from '../components/DiscographyGraph.jsx'
import { LoadingState } from '../components/ErrorState.jsx'
import { api, ApiError, is404 } from '../lib/api.js'

/**
 * The artist page (PLAN §7).
 *
 * The discography is honestly partial and says so. Sonologue materializes
 * what people look up (§12), so this list grows as the alpha is used rather
 * than arriving complete — and a page that quietly showed four albums by a
 * band with twenty would read as a data bug rather than as a design.
 *
 * "Top rated" is part of this screen in the plan and is not here: there are
 * no ratings until RATE-001.
 */
export default function Artist() {
  const { id } = useParams()
  const [artist, setArtist] = useState(null)
  const [ratings, setRatings] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    setArtist(null)
    setRatings(null)
    setError(null)
    api
      .artist(id, { signal: controller.signal })
      .then(setArtist)
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err)
      })
    // A second request for the same reason the album page makes one: the
    // discography is identical for everybody, and the scores over it are not.
    api
      .artistRatings(id, { signal: controller.signal })
      .then(setRatings)
      .catch(() => {
        /* The discography is still worth reading without your scores. */
      })
    return () => controller.abort()
  }, [id])

  useEffect(() => {
    if (artist) document.title = `${artist.name} — Sonologue`
    else if (is404(error)) document.title = 'Not found — Sonologue'
  }, [artist, error])

  // The album page's branch, on its sibling: a 404 is the only error that
  // means the record is not there, and it is the only one a retry cannot fix.
  if (error) {
    return (
      <div className="card">
        <div className="empty-state">
          <h1>{is404(error) ? 'No such artist' : 'Not loaded'}</h1>
          <p>
            {error instanceof ApiError
              ? error.message
              : 'Sonologue could not load this artist just now.'}
          </p>
          {is404(error) ? null : (
            <button
              type="button"
              className="btn btn-tonal"
              onClick={() => window.location.reload()}
            >
              Try again
            </button>
          )}
          <Link className="btn btn-quiet" to="/search">
            Back to search
          </Link>
        </div>
      </div>
    )
  }

  if (!artist) return <LoadingState />

  const facts = [
    artist.artist_type,
    artist.country,
    artist.begin_year ? `since ${artist.begin_year}` : null,
  ].filter(Boolean)

  // The graph's dots: the viewer's scores joined onto albums this page
  // already holds, so the rating payload stays a list of scores and ids.
  const ratingByAlbum = new Map((ratings?.items || []).map((r) => [r.album_public_id, r]))
  // A dot needs a year to stand on; an album MusicBrainz has not dated yet
  // stays in the list below and out of the graph.
  const dots = artist.albums
    .filter((album) => ratingByAlbum.has(album.public_id) && album.year != null)
    .map((album) => ({
      public_id: album.public_id,
      title: album.title,
      year: album.year,
      score: ratingByAlbum.get(album.public_id).score,
      rated_at: ratingByAlbum.get(album.public_id).rated_at,
    }))

  return (
    <>
      <div className="artist-hero fade-up">
        <Avatar name={artist.name} size={72} />
        <div>
          <h1 className="large-title">{artist.name}</h1>
          {artist.disambiguation ? (
            <div className="footnote">{artist.disambiguation}</div>
          ) : null}
          <div className="album-facts">{facts.join(' · ')}</div>
        </div>
      </div>

      {/* No score on an artist page, so nothing to sit under: its own row
          under the hero, above the deep links. */}
      <AddToList targetType="artist" targetPublicId={artist.public_id} subject={artist.name} />

      {/* Only when there is something to list — an empty labeled container
        * is an axe violation (aria-prohibited-attr), found when this page
        * joined the gate. */}
      {artist.deep_links.length ? (
        <div className="deep-links" aria-label="Listen on external services">
          {artist.deep_links.map((link) => (
            <a
              key={link.provider}
              className="deep-link"
              href={link.href}
              target="_blank"
              rel="noreferrer noopener"
            >
              Listen on {link.label} ↗
            </a>
          ))}
        </div>
      ) : null}

      {ratings ? (
        <>
          <div className="section-header">
            <h2 className="title-2">Your ratings</h2>
          </div>
          <div className="card card-pad fade-up">
            {dots.length ? (
              <DiscographyGraph dots={dots} />
            ) : (
              <div className="empty-state">
                <h2>No rated albums here yet</h2>
              </div>
            )}
          </div>
        </>
      ) : null}

      <div className="section-header">
        <h2 className="title-2">Albums</h2>
        <span className="footnote">{artist.albums.length} in Sonologue</span>
      </div>

      {artist.albums.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h2>Nothing fetched yet</h2>
            <p>Search for one of their albums and it will appear here.</p>
          </div>
        </div>
      ) : (
        <div className="card hairline-list fade-up">
          {artist.albums.map((album) => (
            <Link key={album.public_id} className="row" to={`/album/${album.public_id}`}>
              <Artwork
                art={album.artwork}
                title={album.title}
                artist={album.credit_name}
                size={44}
              />
              <div className="row-main">
                <div className="row-title">{album.title}</div>
                <div className="row-sub">
                  {[album.year, album.primary_type].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span className="faint" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </div>
      )}

      <p className="footnote mt-4">
        This discography is what Sonologue has fetched so far, not everything they released. Search
        for anything missing and it will be added.
      </p>
    </>
  )
}
